import { BaseRemoteMethod } from 'eurogard-remote-method';
import { TableEvent } from 'eurogard-data-source/table';
import { camelize, coerceString } from '@rxap/utilities';
import type { OpenApiRemoteMethod } from 'eurogard-open-api-remote-method';
import { PaginatorLike } from 'eurogard-data-source/pagination';

export interface TableRemoteMethodParameters {
  page: number;
  size: number;
  sort: string;
  order: 'desc' | 'asc' | undefined | '';
  filter: string;
}

export interface FilterResponse<Data extends Record<string, any>> {
  maxCount: number;
  size: number;
  page: number;
  entities: Data[];
}

export interface TableAdapterDefaultOptions {
  sort?: string;
  order?: 'asc' | 'desc';
}

export class TableAdapterRemoteMethod<Data extends Record<string, any>>
  extends BaseRemoteMethod<Data[], TableEvent> {

  public static BuildFilter(filterEvent?: string | Record<string, any> | null): string {
    const filter: string[] = [];
    if (!filterEvent || typeof filterEvent === 'string') {
      filter.push('__archived:false');
    } else {
      if (!filterEvent['__archived']) {
        filter.push('__archived:false');
      }
      for (const [ key, value ] of Object.entries(filterEvent)) {
        if (value !== null && value !== undefined && `${value}` !== '') {
          const valueString = coerceString(value);
          if (valueString) {
            filter.push([ key, valueString ].join('%3A'));
          }
        }
      }
    }
    return filter.join('%3B');
  }

  constructor(
    private readonly remoteMethod: OpenApiRemoteMethod<FilterResponse<Data>, TableRemoteMethodParameters>,
    private readonly paginator?: PaginatorLike,
    private readonly options?: TableAdapterDefaultOptions,
  ) {
    super(null, remoteMethod.metadata);
    console.log('DEBUG: table adapter constructor');
  }

  protected async _call(event: TableEvent): Promise<Data[]> {
    console.log('DEBUG: table event', event);
    let sort = event.sort?.active ?? this.options?.sort ?? '__updatedAt';
    const prefix = sort.match(/^(_+)/)?.[1] ?? '';
    sort = prefix + camelize(sort);
    const parameters = {
      page: event.page?.pageIndex ?? 0,
      size: event.page?.pageSize ?? 10,
      sort,
      order: (sort === '__updatedAt' ? 'desc' : event.sort?.direction ?? this.options?.order ?? 'asc'),
      filter: TableAdapterRemoteMethod.BuildFilter(event.filter),
      ...(event.parameters ?? {}),
    };
    if (event.filter && typeof event.filter === 'object' && 'after' in event.filter) {
      parameters.after = event.filter['after'].getTime();
    }
    if (event.filter && typeof event.filter === 'object' && 'before' in event.filter) {
      parameters.before =  event.filter['before'].setHours(23, 59, 59, 999);
    }
    console.log('DEBUG: table parameters', parameters);
    const response = await this.remoteMethod.call({parameters});

    console.log('DEBUG: table response', response);

    if (event.setTotalLength) {
      event.setTotalLength(response.maxCount);
    }

    if (this.paginator) {
      this.paginator.length = response.maxCount;
    }

    return response.entities.map(entity => ({ ...entity, __rowId: entity['uuid'] }));
  }

}

export function TableAdapterFactory(remoteMethod: OpenApiRemoteMethod, paginator?: PaginatorLike) {
  console.log('DEBUG: table adapter factory');
  return new TableAdapterRemoteMethod(remoteMethod, paginator);
}

export function TableAdapterFactoryWithOptions(options: TableAdapterDefaultOptions = {}) {
  return (remoteMethod: OpenApiRemoteMethod, paginator?: PaginatorLike) => {
    return new TableAdapterRemoteMethod(remoteMethod, paginator, options);
  };
}
